import { ICompileService, isArray, ITimeoutService } from "angular";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import { RecordDataSource } from "../../../records/grids";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";

import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";

import { machineConfigurationObjectName } from "../../shared/names";
export { machineConfigurationObjectName as vsObjectName } from "../../shared/names";

import { vsServiceName as MachineDetailTabsServiceName } from "../../shared/components/views/machines/detail/tabs";

import { vsObjectName as machineConfigurationTypeName } from "./machineConfigurationType";
import {
  FormViewInstanceConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import { Machine } from "../models/machine";
import { ServiceResponse } from "../../../records/server/query";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementService,
} from "../../../ui/authorizedUiElement";
import {
  IExpressionFunction,
  IFieldArray,
  IFieldRuntimeObject,
  ITemplateScope,
  IWatcher,
} from "AngularFormly";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { get, set } from "lodash";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";
import * as lookupNames from "./lookup";
import { vsObjectName as epromObjectName } from "../configs/machineEpromConfigurations";
import {
  MachineConfigurationType,
  MachineConfigurationTypeView,
} from "../models/machineConfigurationType";
import { moneyValueGetterFactory } from "../../../records/grids/cells/valueGetters";
import {
  vsServiceName as GratonUtilsServiceName,
  GratonUtilsService,
} from "../utils";
import { Record } from "../../../records/server/crud";

export const machineConfigViewDefaultSort =
  "Status,Location,SerialNumber,-Date,Order";

machineConfigurationConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];

export interface MachineEditFormViewConfig extends FormViewInstanceConfig {
  machineFieldsEditable?: boolean;
}

export function machineConfigurationConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  registerGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
  registerForm(formViewServiceProvider);
}

function registerGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  const moneyValueGetter = moneyValueGetterFactory();

  recordGridViewServiceProvider.addGridConfiguration(
    machineConfigurationObjectName,
    [
      ServerUtilityName,
      GridSettingsServiceName,
      (
        serverUtility: ServerUtility,
        gridSettingsService: GridSettingsService
      ) => {
        return {
          columnDefinitionSet: machineConfigurationObjectName,
          defaultSort: machineConfigViewDefaultSort,
          gridDataSource: new RecordDataSource(
            serverUtility,
            "/api/machines/MachineConfiguration"
          ),
          gridSettings: gridSettingsService.getGridSettings(
            Symbol(machineConfigurationObjectName)
          ),
          nameSingular: "Machine Configuration",
          namePlural: "Machines Configurations",
          showAddButton: false,
          showAddButtonPermission: "machineEdit",
        };
      },
    ]
  );

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(
    machineConfigurationObjectName,
    [
      ColumnDefinitionServiceName,
      ColumnDefinitionSetServiceName,
      "$compile",
      (
        columnDefinitionService: ColumnDefinitionService,
        columnDefinitionSetService: ColumnDefinitionSetService,
        $compile: ICompileService
      ) => {
        const machineConfigurationTypeColumns =
          columnDefinitionSetService.getColumnDefinitionSet(
            machineConfigurationTypeName
          );

        const columns = [
          columnDefinitionService.getNumberColDef({
            field: "Id",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "MachineId",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "MachineConfigurationId",
            hide: true,
          }),
          columnDefinitionService.getDateColDef({
            field: "Date",
            hide: true,
          }),
          columnDefinitionService.getDateColDef({
            field: "InServiceDate",
            header: "In Service Date",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ProjectNumber",
            header: "Project #",
          }),
          columnDefinitionService.getStringColDef({
            field: "Location",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineSectionName",
            header: "Section",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Bank",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Position",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "WarehouseLocation",
            header: "Warehouse Location",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "SerialNumber",
            header: "Serial Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "SlotNumber",
            header: "Machine Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "Status",
            filter: new FilterProvider("Status", $compile)
              .addFilter(
                "=",
                new OptionsFilter({
                  displayName: "=",
                  queryFieldNames: ["Status"],
                  options: [
                    {
                      display: "Floor",
                      value: "Floor",
                    },
                    {
                      display: "Warehoused",
                      value: "Warehoused",
                    },
                    {
                      display: "Decommissioned",
                      value: "Decommissioned",
                    },
                  ],
                })
              )
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineManufacturerName",
            header: "Manufacturer",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineCabinetTypeName",
            header: "Cabinet Type",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineModelName",
            header: "Model",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineGameTypeName",
            header: "Game Type",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CameraCoverage",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "FreePlayEnabled",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "SwiftPayEnabled",
            header: "Swiftpay Enabled",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "AftOptioned",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CardReader",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "ProgressiveController",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "AdjustedByCompliance",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ProgressivePosition",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ProgressiveSerialNumber",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "Leased",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "BinLocation",
            header: "SO # / Bin Location",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "CreditLimit",
            valueGetter: moneyValueGetter,
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "BillLimit",
            valueGetter: moneyValueGetter,
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "TicketAcceptLimit",
            valueGetter: moneyValueGetter,
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "JackpotLimit",
            valueGetter: moneyValueGetter,
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Notes",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CurrentConfiguration",
            header: "Current Configuration",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ChangeType",
            header: "Change Type",
            hide: true,
          }),
          ...machineConfigurationTypeColumns,
        ];

        return columns;
      },
    ]
  );
}

function registerForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(machineConfigurationObjectName, [
    "config",
    FormViewServiceName,
    MachineDetailTabsServiceName,
    FieldConfigurationHelperServiceName,
    MachineLookupManagementServiceName,
    RecordGridViewServiceName,
    GratonUtilsServiceName,
    ServerUtilityName,
    "$timeout",
    (
      config: MachineEditFormViewConfig,
      formViewService: FormViewService,
      machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      machineLookupManagementService: LookupManagementService,
      recordGridViewService: RecordGridViewService,
      gratonUtilsService: GratonUtilsService,
      serverUtility: ServerUtility,
      $timeout: ITimeoutService
    ) => {
      const epromGridConfiguration =
        recordGridViewService.getGridConfiguration(epromObjectName)!;

      return {
        fields: <IFieldArray>[
          fieldHelpers.headerField({
            headerText: "Date/Location",
            key: "DateLocation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("Date"),
                label: "Date",
                required: true,
              }),
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("InServiceDate"),
                label: "In Service Date",
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("ProjectNumber"),
                label: "Project #",
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineSectionId"),
                  label: "Section",
                  expressionProperties: requireIfStatus("Floor"),
                  hideExpression: hideIfNotStatus("Floor"),
                  watcher: watchAndSetUndefinedIfNotStatus("Floor", [
                    getConfigurationFieldKey("MachineSectionId"),
                    getConfigurationFieldKey("MachineSection"),
                  ]),
                  objectName: lookupNames.sectionName,
                }
              ),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Bank"),
                label: "Bank",
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Bank")
                ),
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Position"),
                label: "Position",
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Position")
                ),
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("WarehouseLocation"),
                label: "Warehouse Location",
                expressionProperties: requireIfStatus("Warehoused"),
                hideExpression: hideIfNotStatus("Warehoused"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Warehoused",
                  getConfigurationFieldKey("WarehouseLocation")
                ),
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "EGM Information",
            key: "EGMInformation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getMachineFieldKey("SerialNumber"),
                readonly: !config.machineFieldsEditable,
                label: "Serial Number",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("SlotNumber"),
                label: "Machine Number",
                expressionProperties: requireIfStatus("Floor"),
              }),
              {
                key: getConfigurationFieldKey("Status"),
                name: "Status",
                type: "select",
                templateOptions: {
                  label: "Status",
                  options: [
                    {
                      label: "Floor",
                      value: "Floor",
                    },
                    {
                      label: "Warehoused",
                      value: "Warehoused",
                    },
                    {
                      label: "Decommissioned",
                      value: "Decommissioned",
                    },
                  ],
                  required: true,
                  labelProp: "label",
                  valueProp: "value",
                },
              },
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineManufacturerId"),
                  label: "Manufacturer",
                  required: true,
                  objectName: lookupNames.manufacturerName,
                  readonly: !config.machineFieldsEditable,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineCabinetTypeId"),
                  label: "Cabinet Type",
                  required: true,
                  objectName: lookupNames.cabinetTypeName,
                  readonly: !config.machineFieldsEditable,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineModelId"),
                  label: "Model",
                  objectName: lookupNames.modelName,
                  readonly: !config.machineFieldsEditable,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineGameTypeId"),
                  label: "Game Type",
                  required: true,
                  objectName: lookupNames.gameTypeName,
                  
                }
              ),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("CameraCoverage"),
                label: "Camera Coverage",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("FreePlayEnabled"),
                label: "Free Play Enabled",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("SwiftPayEnabled"),
                label: "Swiftpay Enabled",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("AftOptioned"),
                label: "AFT Optioned",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("CardReader"),
                label: "Card Reader",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("Progressive"),
                label: "Progressive",
                readonly: true,
                watcher: [
                  {
                    listener: (field, value, _, scope) => {
                      field.templateOptions!.readonly = false;
                      $timeout(0).then(() => {
                        set(
                          scope.model ?? {},
                          field.key!,
                          ((<MachineConfigurationType>value)?.ProgressiveGames
                            ?.length ?? 0) > 0
                        );
                        field.templateOptions!.readonly = true;
                      });
                    },
                    expression: (_, scope) => {
                      return (<any>scope).model?.Configuration
                        ?.MachineConfigurationType;
                    },
                  },
                ],
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("ProgressiveController"),
                label: "Progressive Controller",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("AdjustedByCompliance"),
                label: "Adjusted By Compliance",
                defaultValue: false,
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("ProgressivePosition"),
                label: "Position",
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("ProgressiveSerialNumber"),
                label: "Progressive Serial #",
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Game Configurations",
            key: "GameConfigurationsHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              {
                ...fieldConfigurationHelperService.selectAsyncField({
                  key: getConfigurationFieldKey("MachineConfigurationTypeId"),
                  label: "Configuration Type",
                  queryUrl: "/api/machines/MachineConfigurationType",
                  queryBuilder: (search: string) => {
                    return {
                      MachineConfigurationTypeNameContains: search,
                    };
                  },
                  onSelect(selectedItem: any, formModel: any) {
                    if (!selectedItem) {
                      set(
                        formModel,
                        getConfigurationFieldKey("MachineConfigurationType"),
                        undefined
                      );
                      return;
                    }
                    serverUtility
                      .getById<ServiceResponse<MachineConfigurationType>>({
                        url: "/api/machines/MachineConfigurationType",
                        id: selectedItem.Id,
                      })
                      .then((r) => {
                        set(
                          formModel,
                          getConfigurationFieldKey("MachineConfigurationType"),
                          r.Result
                        );
                      });
                  },
                  serverItemMapper: (model: MachineConfigurationTypeView) => {
                    return {
                      Id: model.Id,
                      Name: model.MachineConfigurationTypeName!,
                    };
                  },
                  getDefaultItem: (model: any) =>
                    get(
                      model,
                      getConfigurationFieldKey("MachineConfigurationType")
                    ),
                }),
                expressionProperties: requireIfStatus("Floor"),
              },
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Lease Information",
            key: "LeaseInformationHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("Leased"),
                label: "Leased",
                defaultValue: false,
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "EPROMs",
            key: "EpromHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("BinLocation"),
                label: "SO # / Bin Location",
              }),
            ],
            4
          ),
          fieldConfigurationHelperService.repeaterField({
            key: getConfigurationFieldKey("Eproms"),
            allowAdd: true,
            allowAddBottom: true,
            displayNameSingular: epromGridConfiguration.nameSingular ?? "Eprom",
            displayNamePlural: epromGridConfiguration.namePlural ?? "Eproms",
            repeaterFields: fieldHelpers.columnLayout(
              [
                fieldHelpers.simpleStringField({
                  key: "EpromType",
                  label: "Type",
                  required: true,
                  expressionProperties: {
                    "templateOptions.readonly": (
                      _: any,
                      __: any,
                      scope: ITemplateScope
                    ) => gratonUtilsService.isDefaultEprom(<any>scope.model),
                  },
                }),
                fieldHelpers.simpleStringField({
                  key: "EpromLabel",
                  label: "Eprom",
                }),
                fieldHelpers.simpleStringField({
                  key: "EpromVersion",
                  label: "Version",
                }),
                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  className: "col-xs-6",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                  hideExpression: (_, __, scope): any => {
                    return gratonUtilsService.isDefaultEprom(<any>scope.model);
                  },
                },
              ],
              4
            ),
            showHeader: false,
          }),
          fieldHelpers.headerField({
            headerText: "Limit Settings",
            key: "LimitSettingsHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleCurrencyField({
                key: getConfigurationFieldKey("CreditLimit"),
                label: "Credit Limit",
                min: 0,
              }),
              fieldHelpers.simpleCurrencyField({
                key: getConfigurationFieldKey("BillLimit"),
                label: "Bill Limit",
                min: 0,
              }),
              fieldHelpers.simpleCurrencyField({
                key: getConfigurationFieldKey("TicketAcceptLimit"),
                label: "Ticket Accept Limit",
                min: 0,
              }),
              fieldHelpers.simpleCurrencyField({
                key: getConfigurationFieldKey("JackpotLimit"),
                label: "Jackpot Limit",
                min: 0,
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Hyperlinks",
            key: "HyperlinksHeader",
          }),
          fieldConfigurationHelperService.repeaterField({
            allowAdd: true,
            allowAddBottom: false,
            displayNameSingular: "Hyperlink",
            displayNamePlural: "Hyperlinks",
            label: "Hyperlinks",
            key: "Configuration.Hyperlinks",
            name: "Configuration.Hyperlinks",
            repeaterFields: [
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleStringField({
                    key: "DisplayText",
                    name: "DisplayText",
                    label: "Display Text",
                    required: true,
                  }),
                  fieldHelpers.simpleStringField({
                    key: "Url",
                    name: "Url",
                    label: "URL",
                    required: true,
                  }),
                  {
                    key: "copyDelete",
                    type: "recordRepeaterCopyDelete",
                    templateOptions: {
                      label: " ",
                      allowMove: false,
                    },
                  },
                ],
                4
              ),
            ],
            showHeader: false,
          }),
          fieldHelpers.headerField({
            headerText: "Files",
            key: "FileHeader",
          }),
          {
            key: getConfigurationFieldKey("Files"),
            type: "recordFileUpload",
            templateOptions: {
              label: "Files",
              allowMultiple: true,
              hideLabel: true,
            },
          },
          fieldHelpers.headerField({
            headerText: "Notes",
            key: "NotesHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.textAreaField({
                key: getConfigurationFieldKey("Notes"),
                label: " ",
                rows: 5,
              }),
            ],
            1
          ),
        ],
        name: machineConfigurationObjectName,
        submitForm: (args) =>
          formViewService
            .submitForm<ServiceResponse<Machine>>({
              formObject: args.model,
              formViewController: args.formViewController,
              method: "POST",
              url: "/api/machines/Machine",
            })
            .then((result) =>
              machineDetailTabsService.navigateToChildByIndex(0, {
                machineId: result.Result.Id,
              })
            ),
        title: () => "Machine",
        showButtonsAtTop: true,
      };
    },
  ]);
}

function requireIfStatus(status: string): {
  [key: string]: IExpressionFunction;
} {
  return {
    "templateOptions.required": (_, __, scope) =>
      scope.model?.Configuration.Status === status,
  };
}

function hideIfNotStatus(status: string) {
  return (_: any, __: any, scope: ITemplateScope) =>
    get(scope.model, getConfigurationFieldKey("Status")) !== status;
}

function watchAndSetUndefinedIfNotStatus(
  status: string,
  field: string | string[]
): IWatcher {
  return {
    expression: (_, scope) => scope.model?.Configuration.Status !== status,
    listener: blankFieldListener(field),
  };
}

function blankFieldListener(
  field: string | string[]
): (
  field: IFieldRuntimeObject,
  newValue: any,
  oldValue: any,
  scope: ITemplateScope,
  stopWatching: Function
) => void {
  const fields = isArray(field) ? <string[]>field : [<string>field];
  return (_, newValue, ___, scope) => {
    if (newValue) {
      fields.forEach((f) => set(scope.model ?? {}, f, undefined));
    }
  };
}

function getConfigurationFieldKey(fieldName: string) {
  return `Configuration.${fieldName}`;
}

function getMachineFieldKey(fieldName: string) {
  return `Machine.${fieldName}`;
}
